// §34.3/§34.4 - bay x time grid. `days[0]` is the day the user picks the start
// on (columns = active bays, rows = 30-minute increments across the day's
// window); a long job spills into read-only continuation grids for the
// following spanned days, stacked below. Presentational: all data (day details
// + duration) comes in as props so the slot/span logic - which lives in the
// shared pure functions via dayGridUtils - is directly unit-testable without
// mocking fetch/query (see DayGrid.test.tsx).
import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import type { DayDetail, DaySpan } from '@wrapforce/shared';
import {
  addMinutesIso,
  closestRowIndex,
  dayIndexOfDate,
  isSlotOpen,
  multiDayPlacementInfo,
  resolveMultiDayOpenSlots,
  timeRowsForWindow,
  type MultiDayPlacementInfo,
} from './dayGridUtils';
import { Button } from '@/components/ui/button';
import { FieldError, Label } from '@/components/ui/input';
import { useLocaleFormatters } from '@/lib/format';
import { cn } from '@/lib/cn';

const HATCH_STYLE = { backgroundImage: 'repeating-linear-gradient(45deg, currentColor 0, currentColor 1px, transparent 1px, transparent 6px)' };

type Slot = { bayId: string; startsAt: string };
type Range = { bayId: string; start: string; end: string };

export type ConfirmArgs =
  | { startsAt: string; bayId: string }
  | { startsAt: string; dayBays: Array<{ date: string; bayId: string }> };

/** Work + cure highlight ranges keyed by calendar date, for one preview
 * (selection or hover). `bayFor` decides which column a date highlights in
 * (single bay, or the per-day bay chosen in the split flow). */
function buildRanges(spans: DaySpan[], bayFor: (date: string) => string, cureHoldMinutes: number) {
  const work = new Map<string, Range>();
  for (const s of spans) work.set(s.date, { bayId: bayFor(s.date), start: s.startsAt, end: s.endsAt });
  const cure = new Map<string, Range>();
  const last = spans[spans.length - 1];
  if (last && cureHoldMinutes > 0) {
    cure.set(last.date, { bayId: bayFor(last.date), start: last.endsAt, end: addMinutesIso(last.endsAt, cureHoldMinutes) });
  }
  return { work, cure };
}

function inRange(map: Map<string, Range>, date: string, bayId: string, rowIso: string): boolean {
  const r = map.get(date);
  return !!r && r.bayId === bayId && r.start <= rowIso && rowIso < r.end;
}

export function DayGrid({
  days, durationMinutes, cureHoldMinutes, preferredTime, onConfirm, confirmPending,
}: {
  days: DayDetail[];
  durationMinutes: number;
  cureHoldMinutes: number;
  preferredTime: number | null;
  onConfirm: (args: ConfirmArgs) => void;
  confirmPending?: boolean;
}) {
  const { t } = useTranslation('jobs');
  const { formatDate, formatTimeOfDay } = useLocaleFormatters();
  const day0 = days[0];

  const [selection, setSelection] = useState<Slot | null>(null);
  const [hover, setHover] = useState<Slot | null>(null);
  const [splitMode, setSplitMode] = useState(false);
  const [splitBays, setSplitBays] = useState<Record<string, string>>({});

  const openSlots = useMemo(() => resolveMultiDayOpenSlots(days, durationMinutes), [days, durationMinutes]);

  const selectionInfo = useMemo<MultiDayPlacementInfo | null>(() => {
    if (!selection) return null;
    const info = multiDayPlacementInfo(days, selection.startsAt, selection.bayId, durationMinutes);
    return info.ok ? info : null;
  }, [days, selection, durationMinutes]);

  const hoverInfo = useMemo<MultiDayPlacementInfo | null>(() => {
    if (!hover) return null;
    const info = multiDayPlacementInfo(days, hover.startsAt, hover.bayId, durationMinutes);
    return info.ok ? info : null;
  }, [days, hover, durationMinutes]);

  // Editing the duration after a pick can make the selected start unbookable
  // (or split state stale) - drop the selection so we never confirm a span the
  // grid no longer shows as open.
  useEffect(() => {
    if (selection && !isSlotOpen(openSlots, selection.bayId, selection.startsAt)) {
      setSelection(null);
      setSplitMode(false);
      setSplitBays({});
    }
  }, [openSlots, selection]);

  useEffect(() => {
    setSplitMode(false);
    setSplitBays({});
  }, [durationMinutes]);

  function selectSlot(slot: Slot) {
    setSelection(slot);
    setSplitMode(false);
    setSplitBays({});
  }

  const selectionSpans = selectionInfo?.spans ?? [];
  const hoverSpans = hoverInfo?.spans ?? [];
  const selBayFor = (date: string) => (splitMode ? splitBays[date] ?? selection!.bayId : selection?.bayId ?? '');
  const hovBayFor = () => hover?.bayId ?? '';
  const selectionRanges = useMemo(() => buildRanges(selectionSpans, selBayFor, cureHoldMinutes), [selectionSpans, splitMode, splitBays, selection, cureHoldMinutes]);
  const hoverRanges = useMemo(() => buildRanges(hoverSpans, hovBayFor, cureHoldMinutes), [hoverSpans, hover, cureHoldMinutes]);

  const selEndIdx = selectionSpans.length ? dayIndexOfDate(days, selectionSpans[selectionSpans.length - 1]!.date) : 0;
  const hovEndIdx = hoverSpans.length ? dayIndexOfDate(days, hoverSpans[hoverSpans.length - 1]!.date) : 0;
  const renderedDays = days.slice(0, Math.max(0, selEndIdx, hovEndIdx) + 1);
  const multiDay = renderedDays.length > 1;

  // Whether this job could ever span past day 1 at all - true whenever a
  // continuation day was fetched, regardless of the current hover/selection.
  // The "Day N" headings key off this (not `multiDay`) so day 1's own heading
  // doesn't mount/unmount - and reflow the grid above the cursor - as a hover
  // preview grows or shrinks which later days are rendered.
  const multiDayPossible = days.length > 1;

  const bayName = (id: string) => day0?.bays.find((b) => b.id === id)?.name ?? id;
  const dateLabel = (iso: string) => formatDate(new Date(iso), 'EEE, MMM d');
  const spanDayIndex = (date: string) => (selectionInfo ? selectionInfo.spans.findIndex((s) => s.date === date) + 1 : 0);

  const noCapacityDates = selectionInfo && !selectionInfo.singleBayOk
    ? selectionInfo.perDay.filter((p) => p.bays.length === 0).map((p) => p.startsAt)
    : [];
  const bayFullFailures = selectionInfo && !selectionInfo.singleBayOk && noCapacityDates.length === 0
    ? selectionInfo.singleBayFailures.filter((f) => f.reason === 'bay_conflict')
    : [];

  function startSplit() {
    if (!selectionInfo || !selection) return;
    const init: Record<string, string> = {};
    for (const p of selectionInfo.perDay) {
      init[p.date] = p.date === day0!.date && p.bays.includes(selection.bayId) ? selection.bayId : p.bays[0]!;
    }
    setSplitBays(init);
    setSplitMode(true);
  }

  const canConfirmSingle = !!(selectionInfo && selectionInfo.singleBayOk && !splitMode);
  const canConfirmSplit = !!(
    selectionInfo && !selectionInfo.singleBayOk && splitMode && selectionInfo.spans.every((s) => splitBays[s.date])
  );
  const canConfirm = canConfirmSingle || canConfirmSplit;

  function handleConfirm() {
    if (!selection || !selectionInfo) return;
    if (canConfirmSingle) {
      onConfirm({ startsAt: selection.startsAt, bayId: selection.bayId });
    } else if (canConfirmSplit) {
      onConfirm({ startsAt: selection.startsAt, dayBays: selectionInfo.spans.map((s) => ({ date: s.date, bayId: splitBays[s.date]! })) });
    }
  }

  function renderDay(day: DayDetail, dayIdx: number) {
    const interactive = dayIdx === 0;
    const activeBays = day.bays.filter((b) => b.isActive);
    const rows = timeRowsForWindow(day.window);
    const preferredRowIndex = interactive && preferredTime != null ? closestRowIndex(rows, preferredTime) : null;

    return (
      <div key={day.date} className="flex flex-col gap-1.5">
        {multiDayPossible ? (
          <div className="text-xs font-semibold text-ink">{t('scheduling.dayHeading', { index: dayIdx + 1, date: dateLabel(`${day.date}T00:00:00`) })}</div>
        ) : null}
        {day.window ? (
          <div className="overflow-x-auto border border-border">
            <div
              className="grid"
              style={{ gridTemplateColumns: `72px repeat(${activeBays.length || 1}, minmax(100px, 1fr))`, minWidth: activeBays.length ? undefined : '100%' }}
            >
              <div className="sticky left-0 z-10 border-b border-r border-border bg-surface-2" />
              {activeBays.map((bay) => (
                <div key={bay.id} className="border-b border-border bg-surface-2 px-2 py-1.5 text-center text-[11px] font-bold text-ink">
                  {bay.name}
                </div>
              ))}

              {rows.map((rowIso, rowIndex) => {
                const isPreferredRow = preferredRowIndex === rowIndex;
                return (
                  <div key={rowIso} className="contents">
                    <div
                      className={cn(
                        'sticky left-0 z-10 border-r border-border bg-surface px-2 py-1 text-right text-[10px] text-muted',
                        isPreferredRow ? 'border-t-2 border-t-accent font-bold text-accent-ink' : '',
                      )}
                      title={isPreferredRow ? t('scheduling.preferredTimeMarker', { time: formatTimeOfDay(preferredTime!) }) : undefined}
                    >
                      {formatDate(new Date(rowIso), 'HH:mm')}
                    </div>
                    {activeBays.map((bay) => {
                      const segment = day.segments.find((s) => s.bayId === bay.id && s.startsAt <= rowIso && rowIso < s.endsAt);
                      const open = interactive && isSlotOpen(openSlots, bay.id, rowIso);
                      const isStart = segment?.startsAt === rowIso;

                      const selWork = inRange(selectionRanges.work, day.date, bay.id, rowIso);
                      const selCure = !selWork && inRange(selectionRanges.cure, day.date, bay.id, rowIso);
                      const hovWork = inRange(hoverRanges.work, day.date, bay.id, rowIso);
                      const hovCure = !hovWork && inRange(hoverRanges.cure, day.date, bay.id, rowIso);

                      let cellClass = 'border-b border-border text-[10px]';
                      let label: string | null = null;
                      let style: CSSProperties | undefined;
                      let highlight: string | undefined;

                      if (segment?.kind === 'work') {
                        cellClass = cn(cellClass, 'bg-ink text-surface');
                        if (isStart) label = `#${segment.jobNumber ?? ''} ${segment.customerName ?? ''}`.trim();
                      } else if (segment?.kind === 'cure_hold') {
                        cellClass = cn(cellClass, 'bg-surface-2 text-muted');
                        style = HATCH_STYLE;
                        if (isStart) label = t('scheduling.cure');
                      } else if (selWork) {
                        cellClass = cn(cellClass, 'bg-accent text-accent-ink ring-1 ring-inset ring-accent');
                        highlight = 'selected-work';
                      } else if (selCure) {
                        cellClass = cn(cellClass, 'bg-accent/30 text-accent-ink');
                        style = HATCH_STYLE;
                        highlight = 'selected-cure';
                      } else if (hovWork) {
                        cellClass = cn(cellClass, 'bg-accent/30 text-accent-ink');
                        highlight = 'hover-work';
                      } else if (hovCure) {
                        cellClass = cn(cellClass, 'bg-accent/10 text-accent-ink');
                        style = HATCH_STYLE;
                        highlight = 'hover-cure';
                      } else if (open) {
                        cellClass = cn(cellClass, 'cursor-pointer bg-success/5 hover:bg-success/15');
                      } else if (interactive) {
                        cellClass = cn(cellClass, 'bg-surface-2/60 text-muted');
                      } else {
                        cellClass = cn(cellClass, 'bg-surface');
                      }

                      const commonProps = {
                        'data-testid': `slot-${bay.id}-${rowIso}`,
                        'data-highlight': highlight,
                        className: cn(cellClass, isPreferredRow ? 'border-t-2 border-t-accent' : '', 'h-6 truncate px-1 text-left'),
                        style,
                      };

                      if (!interactive) {
                        return <div key={bay.id} {...commonProps}>{label}</div>;
                      }
                      return (
                        <button
                          key={bay.id}
                          type="button"
                          disabled={!open}
                          data-open={open}
                          onMouseEnter={() => open && setHover({ bayId: bay.id, startsAt: rowIso })}
                          onMouseLeave={() => setHover(null)}
                          onClick={() => open && selectSlot({ bayId: bay.id, startsAt: rowIso })}
                          {...commonProps}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="border border-border bg-surface-2/40 px-3 py-2 text-xs text-muted">{t('scheduling.closedDay')}</div>
        )}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-4">{renderedDays.map(renderDay)}</div>

      {preferredTime != null ? (
        <p className="flex items-center gap-1.5 text-xs text-ink">
          <span aria-hidden className="inline-block h-[3px] w-6 shrink-0 bg-accent" />
          {t('scheduling.preferredTimeMarker', { time: formatTimeOfDay(preferredTime) })}
        </p>
      ) : null}

      {selectionInfo && !selectionInfo.singleBayOk && noCapacityDates.length > 0 ? (
        <FieldError>{t('scheduling.noCapacityOnDays', { dates: noCapacityDates.map(dateLabel).join(', ') })}</FieldError>
      ) : null}

      {bayFullFailures.length > 0 ? (
        <div className="flex flex-col gap-2">
          {bayFullFailures.map((f) => (
            <FieldError key={`${f.date}-${f.bayId}`}>
              {t('scheduling.bayFullOnDays', { bay: bayName(f.bayId), date: dateLabel(`${f.date}T00:00:00`), dayIndex: spanDayIndex(f.date) })}
            </FieldError>
          ))}
          {!splitMode ? (
            <Button size="sm" variant="secondary" className="w-fit" onClick={startSplit}>
              {t('scheduling.splitAcrossBays')}
            </Button>
          ) : null}
        </div>
      ) : null}

      {splitMode && selectionInfo ? (
        <div className="flex flex-col gap-2 border border-border bg-surface-2/40 p-3">
          {selectionInfo.perDay.map((p) => (
            <div key={p.date} className="flex items-center gap-3">
              <Label className="w-32 shrink-0 normal-case">{dateLabel(p.startsAt)}</Label>
              <select
                aria-label={t('scheduling.bayForDay', { date: dateLabel(p.startsAt) })}
                value={splitBays[p.date] ?? ''}
                onChange={(e) => setSplitBays((prev) => ({ ...prev, [p.date]: e.target.value }))}
                className="h-9 w-full border border-border bg-surface px-3 text-sm"
              >
                {p.bays.map((id) => (
                  <option key={id} value={id}>{bayName(id)}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        {selection && selectionInfo ? (
          <span data-testid="selected-slot-summary" className="text-xs text-muted">
            {t('scheduling.selectedSlot', {
              start: formatDate(new Date(selectionInfo.spans[0]!.startsAt), 'MMM d, h:mm a'),
              end: formatDate(new Date(selectionInfo.spans[selectionInfo.spans.length - 1]!.endsAt), multiDay ? 'MMM d, h:mm a' : 'h:mm a'),
            })}
          </span>
        ) : (
          <span data-testid="pick-slot-hint" className="text-xs text-muted">{t('scheduling.pickSlotHint')}</span>
        )}
        <Button size="sm" className="ml-auto" disabled={!canConfirm} loading={confirmPending} onClick={handleConfirm}>
          {t('scheduling.confirmSlot')}
        </Button>
      </div>
    </div>
  );
}
